import { AppBar, Body, PageLayout, Spinner } from 'components/common';
import AlertItem from 'components/notification/AlertItem';
import PullToRefresh from 'components/notification/PullToRefresh';
import PurchaseItem from 'components/notification/PurchaseItem';
import SystemItem from 'components/notification/SystemItem';
import useNotificationAction from 'hooks/notification/useNotificationAction';
import useNotificationList from 'hooks/notification/useNotificationList';
import { useEffect, useRef } from 'react';
import { NOTIFICATION_TABS, type Notification, type NotificationTab } from 'types/notification';
import { sendClickLog } from './notificationService';

const TAB_CLASS = 'whitespace-nowrap rounded-full border-none px-3 py-[6px] typography-body-sm';

function tabLabel(tab: NotificationTab): string {
  if (tab === 'alert') return brsc.notification;
  if (tab === 'system') return 'Team Bermuda';
  const text = brsc[tab];
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const PageNotification: React.FC = () => {
  const listRef = useRef<HTMLDivElement>(null);
  const { tab, setTab, items, loading, loaded, refreshing, observerRef, refresh, restoredScrollTop } = useNotificationList(listRef);
  const action = useNotificationAction();

  // 탭마다 보던 자리를 따로 기억한다.
  const scrollTops = useRef<Partial<Record<NotificationTab, number>>>({});
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!listRef.current) return;
    // 상점 등에 다녀온 첫 렌더에서만 담아 둔 자리로 되돌린다.
    if (!restoredRef.current) {
      restoredRef.current = true;
      listRef.current.scrollTop = restoredScrollTop;
      return;
    }
    listRef.current.scrollTop = scrollTops.current[tab] ?? 0;
  }, [tab, restoredScrollTop]);

  const selectTab = (next: NotificationTab) => {
    if (next === tab || refreshing) return;
    if (listRef.current) scrollTops.current[tab] = listRef.current.scrollTop;
    setTab(next);
  };

  const scrollToTop = () => {
    const list = listRef.current;
    if (!list || list.scrollTop <= 0) return;
    list.scrollTo({ top: 0, behavior: 'smooth' });
    sendClickLog('header_scroll_top');
  };

  const openFaq = () => {
    sendClickLog('faq_click');
    Transition.showPage(Def.PAGE_INQUIRY);
  };

  // 읽지 않은 첫 항목에만 머리말이 붙는다.
  const firstUnreadId = items.find((item) => item.unread)?.id;

  const renderItem = (item: Notification) => {
    const showUnreadHeader = item.id === firstUnreadId;

    if (item.parentCategory === 'alert') {
      return (
        <AlertItem
          key={item.id}
          item={item}
          showUnreadHeader={showUnreadHeader}
          onAvatarClick={() => action.openProfile(item)}
          onActionClick={() => (item.subtype === 'missedCall' ? action.call(item) : action.chat(item))}
        />
      );
    }
    if (item.parentCategory === 'purchase') {
      return (
        <PurchaseItem
          key={item.id}
          item={item}
          showUnreadHeader={showUnreadHeader}
          onShopClick={action.goShop}
          onInquiryClick={action.goInquiry}
          onLuckyboxClick={action.openLuckybox}
          onTimeBuffClick={action.openTimeBuff}
        />
      );
    }
    return (
      <SystemItem
        key={item.id}
        item={item}
        showUnreadHeader={showUnreadHeader}
        onLinkClick={action.followClientLink}
        onCommunityRulesClick={action.goCommunityRules}
        onInquiryClick={action.goInquiry}
      />
    );
  };

  return (
    <PageLayout>
      {/* 앱바를 누르면 목록 맨 위로 — vanilla 의 hNotiListHdr 클릭과 같은 자리다. */}
      <div onClick={scrollToTop}>
        <AppBar title={brsc.notification} />
      </div>
      {/* tabsubcont 가 이미 absolute 라 위치 기준은 그대로 두고 스크롤만 안쪽 목록에 넘긴다. */}
      <Body className="flex flex-col" style={{ overflowY: 'hidden' }}>
        <div className="no-scrollbar flex w-full flex-shrink-0 flex-row items-center gap-2 overflow-x-auto px-4 py-2">
          {NOTIFICATION_TABS.map((name) => (
            <button
              key={name}
              type="button"
              className={`${TAB_CLASS} ${name === tab ? 'bg-btn-bg-primary font-bold text-base-txt-white' : 'bg-btn-bg-normal text-base-txt-primary'}`}
              onClick={() => selectTab(name)}
            >
              {tabLabel(name)}
            </button>
          ))}
        </div>

        <PullToRefresh containerRef={listRef} refreshing={refreshing} onRefresh={refresh} />

        <div ref={listRef} className="flex-1 overflow-y-auto pb-[100px]">
          {items.length > 0 && (
            <>
              <ul className="m-0 flex list-none flex-col p-0">{items.map(renderItem)}</ul>
              <div ref={observerRef} />
            </>
          )}

          {/* 새로 받는 동안. 첫 장은 가운데, 다음 장부터는 목록 끝에 붙인다.
              당겨서 새로고침 중에는 위쪽 로티가 이미 돌고 있어 겹쳐 그리지 않는다. */}
          {loading && !refreshing && (
            <div className={`flex w-full items-center justify-center py-6 ${items.length === 0 ? 'h-full' : ''}`}>
              <Spinner size={32} className="text-base-txt-secondary" />
            </div>
          )}

          {!loading && loaded && items.length === 0 && (
            <div className="flex h-full w-full flex-col items-center justify-center">
              <img src="/imgweb/notification/noti_empty_bell.svg" className="mb-[35px] w-1/2 object-cover" alt="" />
              <span className="typography-body-sm py-3 text-base-txt-secondary">{brsc.noti_nohistory}</span>
            </div>
          )}
        </div>

        <div
          className={`typography-title-md absolute bottom-0 left-0 right-0 flex items-center justify-center rounded-t-lg bg-btn-bg-primary pt-4 text-btn-txt-primary transition-transform duration-200 ease-in-out ${
            tab === 'system' ? 'translate-y-0' : 'translate-y-[60px]'
          }`}
          style={{ paddingBottom: 'calc(var(--navigation-bar-height) + 16px)' }}
          onClick={openFaq}
        >
          FAQ
        </div>
      </Body>
    </PageLayout>
  );
};

export default PageNotification;
